import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import {
  GetTheatricalReleaseQuery,
  GetTheatricalReleaseQueryVariables,
  TheatricalProductsQuery,
  TheatricalProductsQueryVariables,
  TicketSalesPromosQuery,
  TicketSalesPromosQueryVariables,
} from '@/types/codegen-federation'

export const GET_THEATRICAL_RELEASES = gql`
  query getTheatricalReleases($input: TheatricalReleasesInput!) {
    theatricalReleases(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        isFeaturedRelease
        title
      }
    }
  }
`

export const GET_THEATRICAL_RELEASE = gql`
  query getTheatricalRelease($input: TheatricalReleaseInput!, $region: String!) {
    theatricalRelease(input: $input) {
      theatricalRelease {
        id
        movieRating
        theatricalSlug
        title
        longDescription
        shortDescription
        mediumDescription
        hypebotCode
        verticalPoster
        horizontalPoster
        projectSlug
        soldOutShowtimeCount
        backdropImage
        backdropMobileImage
        logoImage
        logoImageWeb
        critics {
          company
          scores {
            name
            value
          }
        }
        theatricalReleaseRegions {
          id
          countryCode
          regionName
          ticketsStatus
        }
        movieDuration
        externalCta {
          id
          title
          subtitle
          ctaText
          ctaUrl
          imageUrl
        }
        region(region: $region) {
          id
          trailerUrl
          seoImage
          releaseDate
          redirectTicketsPage
          ticketsEmergencyRedirectUrl
          freeTicketRequestAmount
          isGroupTicketsEnabled
          ticketsStatus
          trailerThumbnail
          regionName
          countryCode
          useSimpleWaitlistOverlay
          defaultTicketingProviderSlug
          partnerUrl
          faqs {
            question
            answer
          }
        }
      }
    }
  }
`

export async function getTheatricalRelease({
  theatricalSlug,
  client,
  region,
}: {
  theatricalSlug: string | null
  client: ApolloClient<NormalizedCacheObject>
  region?: string
}) {
  if (!theatricalSlug) {
    throw new Error('theatrical slug missing for theatrial release data')
  }
  const { data } = await client.query<GetTheatricalReleaseQuery, GetTheatricalReleaseQueryVariables>({
    query: GET_THEATRICAL_RELEASE,
    variables: {
      input: {
        theatricalSlug,
      },
      region: region?.toUpperCase() ?? 'US',
    },
    fetchPolicy: 'network-only',
  })
  return data?.theatricalRelease?.theatricalRelease
}

export const GET_THEATRICAL_PRODUCTS = gql`
  query theatricalProducts($input: TheatricalProductsInput!) {
    theatricalProducts(input: $input) {
      products {
        id
        title
        theatricalSlug
        amount {
          currencyCode
          currencyAmount
        }
        description
      }
    }
  }
`

export const GET_THEATRICAL_OFFERS = gql`
  query theatricalOffers($input: TheatricalOffersInput!) {
    theatricalOffers(input: $input) {
      offers {
        id
        title
        description
        theatricalSlug
        offerProducts {
          id
          quantity
          product {
            id
            title
            description
            benefits
            theatricalSlug
            amount {
              currencyAmount
              currencyCode
            }
          }
        }
        statistics {
          numSoldLastTwentyFourHours
          numBackers
        }
        amount {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export async function getTheatricalProducts({
  theatricalSlug,
  client,
}: {
  theatricalSlug: string
  client: ApolloClient<NormalizedCacheObject>
}) {
  const { data } = await client.query<TheatricalProductsQuery, TheatricalProductsQueryVariables>({
    query: GET_THEATRICAL_PRODUCTS,
    variables: {
      input: {
        theatricalSlug,
      },
    },
    fetchPolicy: 'network-only',
  })
  return data?.theatricalProducts?.products
}

export const GET_TICKET_SALES_PROMOS = gql`
  query ticketSalesPromos($input: TicketSalesPromosInput!) {
    ticketSalesPromos(input: $input) {
      id
      activePromosList
      sweepstakesSchedule {
        id
        name
        startDate
        endDate
        items {
          ticketType
          entriesPerTicket
          multiplier
          startDate
          endDate
        }
      }
    }
  }
`

export async function getTicketSalesProps({
  theatricalSlug,
  client,
  date,
}: {
  theatricalSlug: string | null
  client: ApolloClient<NormalizedCacheObject>
  date?: string
}) {
  if (!theatricalSlug) {
    throw new Error('theatrical slug missing for theatrial release data')
  }
  const { data } = await client.query<TicketSalesPromosQuery, TicketSalesPromosQueryVariables>({
    query: GET_TICKET_SALES_PROMOS,
    variables: {
      input: {
        projectSlug: theatricalSlug,
        date,
      },
    },
    fetchPolicy: 'network-only',
  })
  return data?.ticketSalesPromos?.sweepstakesSchedule
}
